import { Link, Navigate, Outlet } from "react-router-dom";
import { isAuth } from "../../features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";

import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Typography from "@mui/material/Typography";
import { BrandingLogo } from "../Logo";
import Avatar from "@mui/material/Avatar";
import Card from "@mui/material/Card";
import { SidebarLayout } from "../../features/dashboard/components/Sidebar/SidebarLayout";
import Drawer from "@mui/material/Drawer";
import Popover from "@mui/material/Popover";
import AccountDropdown from "../AccountDropdown";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import { type MouseEvent, useEffect, useState } from "react";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Slide from "@mui/material/Slide";
import { useTreeDataWithPaths } from "../../features/dashboard/components/Sidebar/tree/hooks/useTreeDataWithPaths";
import {
  selectDesktopSidebarOpen,
  selectMobileSidebarOpen,
  setDesktopSidebarOpen,
  setMobileSidebarOpen,
  setTreeDataWithPaths,
} from "../../features/dashboard/layoutSlice";
import { usePathAwareItemSelection } from "../../features/dashboard/hooks/usePathAwareItemSelection";

const drawerWidth = { sm: 300, md: 250, lg: 330 };

const ProtectedDashboardLayout = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const user = useAppSelector(isAuth);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  usePathAwareItemSelection();

  const mobileSidebarOpen = useAppSelector(selectMobileSidebarOpen);
  const desktopSidebarOpen = useAppSelector(selectDesktopSidebarOpen);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    dispatch(setMobileSidebarOpen(!mobileSidebarOpen));
  };

  const handleDesktopDrawerToggle = () => {
    dispatch(setDesktopSidebarOpen(!desktopSidebarOpen));
  };

  const handleAvatarClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const treeDataWithPaths = useTreeDataWithPaths();
  useEffect(() => {
    if (treeDataWithPaths.length > 0) {
      dispatch(setTreeDataWithPaths(treeDataWithPaths));
    }
  }, [treeDataWithPaths, dispatch]);

  return user ? (
    <Box sx={{ background: theme.palette.background.default }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `100%` },
          background: theme.general.logoBg,
        }}
      >
        <Toolbar>
          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDesktopDrawerToggle}
              sx={{ mr: 2 }}
            >
              {desktopSidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
            <Link to={"/"}>
              <Typography variant="h6" noWrap>
                <BrandingLogo />
              </Typography>
            </Link>
          </Box>
          <Avatar
            alt="User Avatar"
            sx={{ background: theme.palette.warning.main, cursor: "pointer" }}
            onClick={handleAvatarClick}
          />
        </Toolbar>
      </AppBar>
      <Box sx={{ display: "flex", height: "100svh" }}>
        {!isMobile && (
          <Slide direction="right" in={desktopSidebarOpen} mountOnEnter unmountOnExit>
            <Card
              sx={{
                boxSizing: "border-box",
                minWidth: drawerWidth,
                maxWidth: drawerWidth,
                mt: 8,
                borderRadius: 0,
                borderTopRightRadius: 8,
                borderBottomRightRadius: 8,
              }}
            >
              <SidebarLayout />
            </Card>
          </Slide>
        )}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              px: 3.5,
              py: 3,
              maxWidth: "110rem",
              mx: "auto",
              mt: "64px",
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileSidebarOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth.sm },
          }}
        >
          <SidebarLayout />
        </Drawer>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        aria-modal
        slotProps={{
          paper: {
            sx: {
              mt: 1,
            },
          },
        }}
      >
        <AccountDropdown onClosePopover={handlePopoverClose} />
      </Popover>
    </Box>
  ) : (
    <Navigate to="/auth/login" replace />
  );
};

export default ProtectedDashboardLayout;
